import React from 'react';
import ReactDOM from 'react-dom';
import { useData, useField, toReportDateTime, reportTimeOfDay, reportWallClock, fromWallClock } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { ViewLayout, PickerModal, Toast } from '../EpcrFields';

// Each button maps to real Procedure.json `category` values (see
// data.options('eProcedures.03')) rather than label keyword-matching, so the
// picker contents are exactly as granular as the NEMSIS catalog itself. The
// four named buckets partition every category except "Other" (4 items);
// "Other" the button falls back to the full, unfiltered catalog so nothing
// in Procedure.json is ever unreachable.
const ADD_BUTTONS = [
  { key: 'airway', label: 'Airway', kind: 'procedure', categories: ['Airway, Basic', 'Airway, Advanced'] },
  { key: 'critical', label: 'Critical care', kind: 'procedure', categories: ['Assessment', 'Environmental', 'Gastrointestinal', 'Glycemic Management', 'Immobilization', 'Pain', 'Patient Positioning', 'Spinal Procedures', 'Transfer of Care', 'Vital Signs', 'Wound Care'] },
  { key: 'cardio', label: 'Defib / Cardio', kind: 'procedure', categories: ['Cardiac', 'Assessment, Cardiac'] },
  { key: 'iv', label: 'IV therapy', kind: 'procedure', categories: ['Vascular Access', 'Administration (Drug, fluid, gas)'] },
  { key: 'medication', label: 'Medication', kind: 'medication', categories: null },
  { key: 'other', label: 'Other', kind: 'procedure', categories: null },
];

function toInputDateTimeValue(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function EpcrViewFlowchart() {
  const data = useData();
  const procedures = useField("eProcedures/eProcedures.ProcedureGroup") || [];
  const medsRaw = useField("eMedications/eMedications.MedicationGroup");
  const meds = Array.isArray(medsRaw) ? medsRaw : (medsRaw ? [medsRaw] : []);
  const devices = useField("eDevice/eDevice.DeviceGroup") || [];

  const [pickerFor, setPickerFor] = React.useState(null);
  const [editTarget, setEditTarget] = React.useState(null);
  const [toast, setToast] = React.useState(null);

  const events = React.useMemo(() => {
    const all = [];
    (Array.isArray(procedures) ? procedures : []).forEach((p, idx) => {
      all.push({
        time: p["eProcedures.01"],
        kind: "proc",
        label: "Procedure",
        title: data.label("eProcedures.03", p["eProcedures.03"]) || p["eProcedures.03"],
        sub: `Attempt ${p["eProcedures.05"] || 1} · ${data.label("eProcedures.06", p["eProcedures.06"]) || "—"}`,
        meta: data.label("eProcedures.07", p["eProcedures.07"]),
        groupPrefix: "eProcedures",
        groupIndex: idx,
        timePath: `eProcedures/eProcedures.ProcedureGroup/${idx}/eProcedures.01`,
      });
    });
    meds.forEach((m, idx) => {
      all.push({
        time: m["eMedications.01"],
        kind: "med",
        label: "Medication",
        title: data.label("eMedications.03", m["eMedications.03"]) || m["eMedications.03"],
        sub: `${m["eMedications.DosageGroup"]?.["eMedications.05"] || ""} ${data.label("eMedications.06", m["eMedications.DosageGroup"]?.["eMedications.06"]) || ""} · ${data.label("eMedications.07", m["eMedications.07"]) || ""}`,
        meta: data.label("eMedications.08", m["eMedications.08"]),
        groupPrefix: "eMedications",
        groupIndex: idx,
        timePath: `eMedications/eMedications.MedicationGroup/${idx}/eMedications.01`,
      });
    });
    (Array.isArray(devices) ? devices : []).forEach((d, idx) => {
      all.push({
        time: d["eDevice.02"],
        kind: "device",
        label: "Device",
        title: data.label("eDevice.03", d["eDevice.03"]) || "Cardiac monitor event",
        sub: `Serial ${d["eDevice.01"]}`,
        meta: d["eDevice.WaveformGroup"]?.["eDevice.04"] ? "Waveform attached" : "",
        groupPrefix: "eDevice",
        groupIndex: idx,
        timePath: `eDevice/eDevice.DeviceGroup/${idx}/eDevice.02`,
      });
    });
    return all.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  }, [procedures, meds, devices, data]);

  const fmtTime = (iso) => reportTimeOfDay(iso, false);

  const sections = events.map((e, i) => ({
    id: `flow-${i}`,
    label: `${fmtTime(e.time)} · ${e.title}`,
  }));

  const pickerOptions = React.useMemo(() => {
    if (!pickerFor) return [];
    const elem = pickerFor.kind === "medication" ? "eMedications.03" : "eProcedures.03";
    const all = data.options(elem) || [];
    if (!pickerFor.categories) return all;
    const set = new Set(pickerFor.categories);
    return all.filter(o => set.has(o.category));
  }, [pickerFor, data]);

  // Time is always assumed "now" at the moment an item is picked — the user
  // can correct it afterward by clicking the resulting card (EditTimeModal).
  const handlePick = (code) => {
    const now = toReportDateTime(new Date());
    if (pickerFor.kind === "medication") {
      const newGroup = { "eMedications.01": now, "eMedications.02": "9923001", "eMedications.03": code };
      data.setReportPath("eMedications/eMedications.MedicationGroup", [...meds, newGroup]);
      setToast("Medication added to flowchart.");
    } else {
      const newGroup = {
        "eProcedures.01": now,
        "eProcedures.02": "9923001",
        "eProcedures.03": code,
        "eProcedures.05": "1",
        "eProcedures.06": "9923003",
      };
      data.setReportPath("eProcedures/eProcedures.ProcedureGroup", [...(Array.isArray(procedures) ? procedures : []), newGroup]);
      setToast(`${pickerFor.label} procedure added.`);
    }
    setPickerFor(null);
  };

  const addBar = (
    <div className="add-bar" style={{ flexWrap: "wrap" }}>
      {ADD_BUTTONS.map(btn => (
        <button key={btn.key} className="btn-primary" onClick={() => setPickerFor(btn)}>
          <Icons.plus size={14} /> {btn.label}
        </button>
      ))}
    </div>
  );

  return (
    <ViewLayout sections={sections} addBar={addBar} hideNav>
      <div className="tl">
        {events.map((e, i) => (
          <div className="tl-item" key={i} id={`flow-${i}`} data-section={`flow-${i}`}
               data-group-prefix={e.groupPrefix} data-group-index={e.groupIndex}
               style={{ scrollMarginTop: 20 }}>
            <div className="tl-time">
              {fmtTime(e.time)}
              <span className="ms">{i === 0 ? "T+0" : `+${diffMin(events[0].time, e.time)}`}</span>
            </div>
            <div className={"tl-dot " + (e.kind === "med" ? "blue" : e.kind === "device" ? "red" : "green")} />
            <div className="tl-card" onClick={() => setEditTarget(e)} title="Click to edit time">
              <span className={"kind " + (e.kind === "med" ? "med" : e.kind === "proc" ? "proc" : "airway")}>{e.label}</span>
              <div>
                <div className="title">{e.title}</div>
                {e.sub && <div className="sub">{e.sub}</div>}
              </div>
              <div className="meta">{e.meta || ""}</div>
            </div>
          </div>
        ))}
        {events.length === 0 && (
          <div className="epcr-list-empty">No procedures, medications, or device events recorded</div>
        )}
      </div>

      {pickerFor && (
        <PickerModal
          title={`Add ${pickerFor.label}`}
          elem={pickerFor.kind === "medication" ? "eMedications.03" : "eProcedures.03"}
          options={pickerOptions}
          onClose={() => setPickerFor(null)}
          onSelect={handlePick}
        />
      )}

      {editTarget && (
        <EditTimeModal event={editTarget} onClose={() => setEditTarget(null)} />
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </ViewLayout>
  );
}

function EditTimeModal({ event, onClose }) {
  const data = useData();
  const [value, setValue] = React.useState(() => reportWallClock(event.time) || toInputDateTimeValue(new Date()));

  const handleSave = () => {
    if (value) {
      data.setReportPath(event.timePath, fromWallClock(value, event.time));
    }
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="epcr-pm-back" onClick={onClose}>
      <div className="epcr-te epcr-root" onClick={e => e.stopPropagation()}>
        <div className="epcr-te-head">
          <h3>Edit Time</h3>
          <button className="epcr-pm-close" onClick={onClose}><Icons.close size={24} /></button>
        </div>
        <div className="epcr-te-body">
          <div className="epcr-te-label">{event.title}</div>
          <input
            autoFocus
            type="datetime-local"
            step="1"
            className="epcr-te-input"
            value={value}
            onChange={e => setValue(e.target.value)}
          />
          <button type="button" className="btn-secondary" onClick={() => setValue(toInputDateTimeValue(new Date()))}>Now</button>
        </div>
        <div className="epcr-te-foot">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function diffMin(a, b) {
  if (!a || !b) return "—";
  const ms = new Date(b) - new Date(a);
  return Math.round(ms / 60000) + "m";
}
